/**
 * WaveFormationPage — "NFL play" cohort formation view.
 *
 * Route: /waves/:id/formation
 *
 * A brand rep who just sent an RFP wants ONE page that says "this is who's
 * firing when, in what order". Text lists don't sell it. This page does.
 *
 * Layout: 4 left-to-right bands — each band is a ROLE column on a dark
 * football-field backdrop. Avatars are circular, colour-ringed by platform,
 * and arranged in a staggered vertical formation within their column so it
 * reads like a play diagram instead of a spreadsheet.
 *
 * Animation: an ignition pulse emanates from Patient Zero (leftmost), a
 * propagation line sweeps right through Amplifiers → Prompters → Closers,
 * then loops.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import CreatorAvatar from '../components/creators/CreatorAvatar';
import {
  ArrowLeft, Play, Activity, Users, Zap, TrendingUp, Loader2,
  Share2, Check, Clock, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLUMNS = [
  { id: 'patient_zero',       label: 'Ignition',    sub: 'Patient-Zero',  color: '#FF4D94', icon: Zap },
  { id: 'amplifier',          label: 'Amplifiers',  sub: 'Spread',        color: '#00D4FF', icon: TrendingUp },
  { id: 'engagement_prompter', label: 'Prompters',  sub: 'Comment-drive', color: '#FFC83D', icon: Activity },
  { id: 'closer',             label: 'Closers',     sub: 'Late-burst',    color: '#4ade80', icon: Play },
];

const PLATFORM_COLOR = {
  tiktok: '#FE2C55', instagram: '#E1306C', youtube: '#FF0000',
  linkedin: '#0A66C2', twitter: '#1DA1F2',
};


export default function WaveFormationPage() {
  const { id } = useParams();
  const [wave, setWave] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(null);
  // iter 63: "Simulate Wave" investor-demo mode — triggers a scripted replay
  // of the wave with metric counters ticking up column-by-column.
  const [simulating, setSimulating] = useState(false);
  const [simStep, setSimStep] = useState(-1); // -1 idle, 0..3 = active column, 4 = done

  useEffect(() => {
    (async () => {
      try {
        const [w, a] = await Promise.all([
          axios.get(`${API}/api/brand/waves/${id}`, { withCredentials: true }),
          axios.get(`${API}/api/brand/waves/${id}/assignments`, { withCredentials: true }),
        ]);
        setWave(w.data);
        setAssignments(a.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load wave.');
      }
    })();
  }, [id]);

  // Bucket assignments by role. If the data is sparse (majority have no role
  // set, common for legacy waves), synthesize roles from follower-count +
  // burst-number so the formation always reads as a formation, not a stack.
  const buckets = useMemo(() => {
    const out = Object.fromEntries(ROLE_COLUMNS.map(c => [c.id, []]));
    const items = assignments || [];
    const nullRate = items.length ? items.filter(a => !a.role).length / items.length : 0;

    if (nullRate >= 0.8 && items.length >= 4) {
      // Synthesize: top 20% by follower count → patient_zero,
      // next 50% → amplifier, next 20% → engagement_prompter, bottom 10% → closer.
      const sorted = [...items].sort((x, y) =>
        (y.follower_count || 0) - (x.follower_count || 0) ||
        (x.burst_number || 1) - (y.burst_number || 1)
      );
      const n = sorted.length;
      const q1 = Math.max(1, Math.floor(n * 0.20));
      const q2 = q1 + Math.max(1, Math.floor(n * 0.50));
      const q3 = q2 + Math.max(1, Math.floor(n * 0.20));
      sorted.forEach((a, i) => {
        const role = i < q1 ? 'patient_zero' : i < q2 ? 'amplifier' : i < q3 ? 'engagement_prompter' : 'closer';
        out[role].push({ ...a, role, __synthesized: true });
      });
    } else {
      items.forEach(a => {
        const role = a.role && out[a.role] ? a.role : 'amplifier';
        out[role].push(a);
      });
    }

    Object.values(out).forEach(list => list.sort((x, y) =>
      (x.burst_number || 1) - (y.burst_number || 1) ||
      (y.follower_count || 0) - (x.follower_count || 0)
    ));
    return out;
  }, [assignments]);

  const totalCreators = assignments?.length || 0;
  const postedCount = useMemo(() => (assignments || []).filter(a => a.actual_post_time).length, [assignments]);

  // Projected reach per column. Consumes the synthesized `buckets` so null-role
  // waves (common on legacy data) still distribute reach across all 4 roles.
  const columnReach = useMemo(() => {
    const ROLE_MULT = { patient_zero: 1.0, amplifier: 1.4, engagement_prompter: 0.7, closer: 1.1 };
    const ROLE_BASE = { patient_zero: 280_000, amplifier: 150_000, engagement_prompter: 95_000, closer: 120_000 };
    const out = {};
    for (const col of ROLE_COLUMNS) {
      const list = buckets[col.id] || [];
      const raw = list.reduce((sum, a) => {
        const fc = a.follower_count || ROLE_BASE[col.id];
        return sum + fc;
      }, 0);
      out[col.id] = Math.round(raw * (ROLE_MULT[col.id] || 1));
    }
    return out;
  }, [buckets]);
  const totalReach = useMemo(() => Object.values(columnReach).reduce((s, v) => s + v, 0), [columnReach]);

  const runSimulation = () => {
    if (simulating) return;
    setSimulating(true);
    setSimStep(0);
    // Step through each column with staggered timing — ignition flares,
    // then amplifiers sweep, then prompters, then closers, ~1.4s per step.
    const timers = [];
    for (let i = 1; i <= ROLE_COLUMNS.length; i++) {
      timers.push(setTimeout(() => setSimStep(i), i * 1400));
    }
    timers.push(setTimeout(() => { setSimulating(false); setSimStep(-1); }, (ROLE_COLUMNS.length + 1) * 1400));
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center" data-testid="formation-error">
          <AlertCircle className="h-8 w-8 text-[#FF6B6B] mx-auto mb-3" />
          <p className="text-white font-black">{error}</p>
          <Link to="/campaigns" className="inline-block mt-5 text-[11px] font-bold text-[#00D4FF] hover:underline">
            Back to Campaigns →
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  if (!wave || !assignments) {
    return (
      <DashboardLayout>
        <div className="p-16 text-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
          Loading formation…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-screen flex flex-col bg-[#0A0A0A] overflow-hidden" data-testid="wave-formation-page">
        {/* ── Header (compact, single-row) ── */}
        <div className="px-5 lg:px-8 pt-4 pb-3 border-b border-[#1F1F1F] flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <Link to={`/waves/${id}/control`} className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-gray-500 hover:text-[#00D4FF] transition-colors" data-testid="formation-back-link">
                <ArrowLeft className="h-3 w-3" /> Wave Control Room
              </Link>
              <h1 className="text-xl lg:text-2xl font-black text-white tracking-tight leading-tight truncate" data-testid="formation-title">
                {wave.name || wave.goal || 'Wave Formation'}
              </h1>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Roll-out order · {totalCreators} creators across {ROLE_COLUMNS.length} roles · hover an avatar for details
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Pill icon={Check}      label="Posted"     value={`${postedCount}/${totalCreators}`} color="#4ade80" />
              <Pill icon={TrendingUp} label="Est. Reach" value={fmtReach(totalReach)} color="#00D4FF" />
              <Pill icon={Activity}   label="Status"     value={(wave.status || 'scheduled').toUpperCase()} color="#FF4D94" />
              <button
                onClick={runSimulation}
                disabled={simulating || totalCreators === 0}
                data-testid="simulate-wave-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider text-black hover:brightness-110 disabled:opacity-60 transition-all"
                style={{
                  background: simulating ? '#FFC83D' : '#00D4FF',
                  boxShadow: simulating ? '0 0 18px rgba(255,200,61,0.55)' : '0 0 14px rgba(0,212,255,0.45)',
                }}
              >
                {simulating ? <Activity className="h-3 w-3 animate-pulse" /> : <Play className="h-3 w-3" />}
                {simulating ? 'Playing' : 'Simulate'}
              </button>
              <Link
                to={`/waves/${id}/control`}
                data-testid="formation-go-control"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#1F1F1F] text-white font-black text-[10px] uppercase tracking-wider hover:border-[#00D4FF] hover:text-[#00D4FF] transition-all"
              >
                Live Engine
              </Link>
            </div>
          </div>
        </div>

        {/* ── The field — fills all remaining viewport, no scroll ── */}
        <div className="relative flex-1 min-h-0 px-3 lg:px-6 py-3 overflow-hidden">
          <FieldBackdrop />
          <IgnitionPulse />

          <div className="relative z-10 grid grid-cols-4 gap-2 lg:gap-4 h-full">
            {ROLE_COLUMNS.map((col, idx) => (
              <RoleColumn
                key={col.id}
                col={col}
                assignments={buckets[col.id]}
                index={idx}
                onHover={setHovered}
                reach={columnReach[col.id] || 0}
                active={simStep >= idx + 1}
                igniting={simStep === idx + 1}
              />
            ))}
          </div>

          {/* Particle propagation: dots travel column-to-column during sim */}
          <ParticleFlow simulating={simulating} simStep={simStep} />
        </div>

        {hovered && <CreatorPeek a={hovered} onClose={() => setHovered(null)} />}

        <style>{styles}</style>
      </div>
    </DashboardLayout>
  );
}

function RoleColumn({ col, assignments, index, onHover, reach, active, igniting }) {
  const Icon = col.icon;
  const count = assignments.length;
  // Adaptive packing — keep all creators above the fold no matter the count.
  // Inner-grid columns scale up as bucket grows so a 30-creator column packs
  // into 3 rows × 10 cols instead of a 30-deep stack that scrolls off screen.
  const innerCols = count <= 4 ? 1 : count <= 10 ? 2 : count <= 18 ? 3 : 4;
  const avatarSize = count <= 4 ? 56 : count <= 10 ? 44 : count <= 18 ? 36 : 28;
  const showLabel = avatarSize >= 40; // hide name labels when avatars get tight
  return (
    <div
      className="relative transition-all duration-500 flex flex-col h-full min-h-0"
      data-testid={`formation-col-${col.id}`}
      style={{
        opacity: active === false && igniting === false ? 1 : 1, // never dim — always visible
        transform: igniting ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      {/* Column header — compact pill row */}
      <div className="relative mb-3 text-center flex-shrink-0">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full transition-all"
          style={{
            background: igniting ? `${col.color}35` : `${col.color}18`,
            border: `1px solid ${igniting ? col.color : `${col.color}45`}`,
            boxShadow: igniting ? `0 0 18px ${col.color}aa` : 'none',
          }}
        >
          <Icon className="h-3 w-3" style={{ color: col.color }} />
          <span className="text-[9px] font-black uppercase tracking-wider" style={{ color: col.color }}>
            {col.label}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-[9px] text-gray-600 uppercase tracking-wider">{col.sub}</span>
          <span className="text-[9px] text-white font-black tabular-nums">· {count}</span>
          <span
            className="text-[9px] font-black tabular-nums px-1.5 py-0 rounded-full"
            style={{ color: col.color, border: `1px solid ${col.color}33`, background: 'rgba(255,255,255,0.03)' }}
            data-testid={`column-reach-${col.id}`}
          >
            ~{fmtReach(reach)}
          </span>
        </div>
      </div>

      {/* Adaptive avatar grid — fills available height, no overflow */}
      <div
        className="flex-1 min-h-0 overflow-hidden grid content-start gap-2"
        style={{ gridTemplateColumns: `repeat(${innerCols}, minmax(0, 1fr))` }}
      >
        {assignments.map((a, i) => (
          <FormationAvatar
            key={a.id || `${col.id}-${i}`}
            a={a}
            col={col}
            index={i}
            columnIndex={index}
            bucketSize={count}
            onHover={onHover}
            igniting={igniting}
            size={avatarSize}
            showLabel={showLabel}
            useOffset={count <= 6}
          />
        ))}
        {count === 0 && (
          <div className="text-center text-[10px] text-gray-700 py-4 px-2 rounded-lg border border-dashed border-[#1F1F1F] col-span-full">
            No creators in this role
          </div>
        )}
      </div>
    </div>
  );
}

function FormationAvatar({ a, col, index, columnIndex, bucketSize, onHover, igniting, size = 48, showLabel = true, useOffset = true }) {
  // Sinusoidal horizontal offset within the column — echoes an actual waveform.
  // Disabled when the column is dense (useOffset=false) so packed avatars stay
  // grid-aligned and never collide.
  const amplitude = (col.id === 'amplifier') ? 22 : 10;
  const phase = bucketSize > 1 ? (index / (bucketSize - 1)) * Math.PI * 2 : 0;
  const offsetX = useOffset ? Math.round(Math.sin(phase + columnIndex * 0.6) * amplitude) : 0;

  const platform = (a.platform || '').toLowerCase();
  const platformColor = PLATFORM_COLOR[platform] || '#888';
  const isPosted = !!a.actual_post_time;
  const delay = columnIndex * 0.15 + index * 0.05;
  const innerSize = size;
  const badgeSize = Math.max(12, Math.round(size * 0.32));

  return (
    <div
      className={`relative flex flex-col items-center cursor-pointer group animate-formation-in ${igniting ? 'animate-igniting' : ''}`}
      style={{
        transform: `translateX(${offsetX}px)`,
        animationDelay: `${delay}s`,
      }}
      onMouseEnter={() => onHover(a)}
      onMouseLeave={() => onHover(null)}
      data-testid={`formation-avatar-${a.id || a.creator_handle}`}
    >
      {/* Ring: platform colour outside, status state inside */}
      <div className="relative">
        <div
          className="rounded-full p-[2px] transition-transform group-hover:scale-[1.08]"
          style={{
            background: `conic-gradient(from 0deg, ${col.color}, ${platformColor})`,
            boxShadow: igniting ? `0 0 32px ${col.color}cc, 0 0 18px ${col.color}66` : `0 0 12px ${col.color}40`,
          }}
        >
          <div className="rounded-full bg-[#0A0A0A] p-[2px]">
            <CreatorAvatar
              handle={a.creator_handle}
              name={a.creator_name}
              src={a.avatar_url}
              size={innerSize}
              className="rounded-full"
            />
          </div>
        </div>
        {isPosted && (
          <div
            className="absolute -bottom-0.5 -right-0.5 rounded-full flex items-center justify-center"
            style={{ width: badgeSize, height: badgeSize, background: '#4ade80', border: '2px solid #0A0A0A' }}
          >
            <Check className="h-3 w-3 text-black" strokeWidth={3} />
          </div>
        )}
        {!isPosted && a.scheduled_post_time && (
          <div
            className="absolute -bottom-0.5 -right-0.5 rounded-full flex items-center justify-center"
            style={{ width: badgeSize, height: badgeSize, background: '#0F0F0F', border: `2px solid ${col.color}` }}
          >
            <Clock className="h-2 w-2" style={{ color: col.color }} />
          </div>
        )}
      </div>

      {showLabel && (
        <>
          <p className="text-[10px] text-white font-black mt-1 text-center max-w-[90px] truncate">
            {a.creator_name || a.creator_handle}
          </p>
          <p className="text-[8px] text-gray-500 tabular-nums">
            Burst {a.burst_number || 1}
          </p>
        </>
      )}
    </div>
  );
}

// ══ Particle flow ══

function ParticleFlow({ simulating, simStep }) {
  // Column x-positions (approximate centers at 12.5/37.5/62.5/87.5%).
  // Shown permanently in idle (gentle), kicked into a scripted burst during sim.
  if (!simulating && simStep < 0) {
    return <AmbientFlow />;
  }
  const colCenters = [12.5, 37.5, 62.5, 87.5];
  const srcIdx = Math.max(0, simStep - 1);
  const dstIdx = Math.min(3, simStep);
  if (srcIdx === dstIdx) return null;
  const x1 = colCenters[srcIdx];
  const x2 = colCenters[dstIdx];
  const gradId = `flow-grad-${simStep}`;
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[6]" aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={ROLE_COLUMNS[srcIdx].color} stopOpacity="0.9" />
          <stop offset="1" stopColor={ROLE_COLUMNS[dstIdx].color} stopOpacity="0.9" />
        </linearGradient>
      </defs>
      <line x1={`${x1}%`} y1="46%" x2={`${x2}%`} y2="46%"
            stroke={`url(#${gradId})`} strokeWidth="2.5"
            strokeDasharray="4 6" className="animate-prop-line" />
      {/* 3 traveling particles along the arc */}
      {[0, 1, 2].map(i => (
        <circle key={i} r="4"
          fill={ROLE_COLUMNS[dstIdx].color}
          style={{ filter: `drop-shadow(0 0 6px ${ROLE_COLUMNS[dstIdx].color})` }}
          className="animate-particle">
          <animate attributeName="cx" from={`${x1}%`} to={`${x2}%`} dur="1.3s" begin={`${i * 0.25}s`} repeatCount="1" />
          <animate attributeName="cy" from="46%" to="46%" dur="1.3s" begin={`${i * 0.25}s`} repeatCount="1" />
          <animate attributeName="opacity" from="0" to="1" dur="0.2s" begin={`${i * 0.25}s`} fill="freeze" />
          <animate attributeName="opacity" from="1" to="0" dur="0.2s" begin={`${i * 0.25 + 1.1}s`} fill="freeze" />
        </circle>
      ))}
    </svg>
  );
}

function AmbientFlow() {
  // Gentle always-on particle trail across the 4 column centers, softer than sim.
  const colCenters = [12.5, 37.5, 62.5, 87.5];
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-[4] opacity-60" aria-hidden>
      <defs>
        <linearGradient id="ambient-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FF4D94" stopOpacity="0.35" />
          <stop offset="0.33" stopColor="#00D4FF" stopOpacity="0.35" />
          <stop offset="0.66" stopColor="#FFC83D" stopOpacity="0.35" />
          <stop offset="1" stopColor="#4ade80" stopOpacity="0.35" />
        </linearGradient>
      </defs>
      <line x1={`${colCenters[0]}%`} y1="46%" x2={`${colCenters[3]}%`} y2="46%"
            stroke="url(#ambient-grad)" strokeWidth="1.5"
            strokeDasharray="3 10" className="animate-ambient-line" />
    </svg>
  );
}

// ══ Number formatter ══

function fmtReach(n) {
  const v = Number(n || 0);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(v);
}

// ══ Backdrop ══

function FieldBackdrop() {
  // Subtle football-field vibe: a dark gradient with faint yard lines + hashmarks.
  // iter 63: the radial gradient now breathes (opacity pulse) on a 6s cycle to
  // reinforce the "living formation" feel without shifting any geometry.
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <div className="absolute inset-0 animate-backdrop-breathe"
           style={{ background: 'radial-gradient(ellipse at center, #0a1218 0%, #050608 70%)' }} />
      {/* Vertical lines at 25/50/75 */}
      {[25, 50, 75].map(pct => (
        <div key={pct} className="absolute top-0 bottom-0 w-px"
             style={{ left: `${pct}%`, background: `rgba(255,255,255,0.04)` }} />
      ))}
      {/* Hash rows (horizontal) */}
      {[15, 35, 65, 85].map(pct => (
        <div key={pct} className="absolute left-0 right-0 h-px"
             style={{ top: `${pct}%`, background: `rgba(255,255,255,0.02)` }} />
      ))}
    </div>
  );
}

function IgnitionPulse() {
  // Pulsing rings that emanate from the leftmost column (Ignition / Patient Zero).
  return (
    <div className="absolute left-[8%] top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden>
      {[0, 1, 2].map(i => (
        <div key={i} className="absolute rounded-full border animate-ignition-pulse"
             style={{
               width: 80, height: 80,
               borderColor: '#FF4D9444',
               top: -40, left: -40,
               animationDelay: `${i * 0.8}s`,
             }} />
      ))}
    </div>
  );
}

function Pill({ icon: Icon, label, value, color = '#fff' }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-semibold bg-[#0F0F0F] border border-[#1F1F1F]">
      <Icon className="h-3 w-3" style={{ color }} />
      <span className="text-gray-500 uppercase tracking-wider">{label}</span>
      <span className="font-black tabular-nums" style={{ color }}>{value}</span>
    </span>
  );
}

function CreatorPeek({ a, onClose }) {
  // Floating card on hover. Positioned in bottom-right so it never covers the formation.
  return (
    <div
      className="fixed bottom-8 right-8 z-40 rounded-xl w-[300px] p-4 animate-fade-in"
      style={{ background: '#0F0F0F', border: '1px solid #1F1F1F', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
      onMouseLeave={onClose}
      data-testid="creator-peek"
    >
      <p className="text-[9px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold mb-2">Cohort member</p>
      <h3 className="text-sm font-black text-white">{a.creator_name || a.creator_handle}</h3>
      <p className="text-[11px] text-gray-500 mt-0.5">{a.creator_handle}</p>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <MiniStat label="Role" value={(a.role || 'amplifier').replace(/_/g, ' ')} />
        <MiniStat label="Burst" value={a.burst_number || 1} />
        <MiniStat label="Platform" value={a.platform} />
        <MiniStat label="Match" value={`${Math.round((a.match_score || 0.78) * 100)}%`} color="#4ade80" />
      </div>
      {a.scheduled_post_time && (
        <p className="text-[10px] text-gray-500 mt-3">
          <Clock className="h-3 w-3 inline mr-1" />
          Scheduled: {new Date(a.scheduled_post_time).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function MiniStat({ label, value, color = '#fff' }) {
  return (
    <div className="rounded-lg p-2" style={{ background: '#0A0A0A', border: '1px solid #1F1F1F' }}>
      <p className="text-[8px] uppercase tracking-wider text-gray-600">{label}</p>
      <p className="text-[11px] font-black truncate" style={{ color }}>{value}</p>
    </div>
  );
}

const styles = `
@keyframes formation-in {
  from { opacity: 0; transform: translate(var(--tx, 0px), -14px) scale(0.85); }
  to   { opacity: 1; transform: translateX(var(--tx, 0px)) scale(1); }
}
.animate-formation-in {
  animation: formation-in 0.55s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
@keyframes ignition-pulse {
  0%   { transform: scale(0.6); opacity: 0.9; }
  100% { transform: scale(3); opacity: 0; }
}
.animate-ignition-pulse {
  animation: ignition-pulse 2.4s ease-out infinite;
}
@keyframes prop-line {
  0%   { stroke-dashoffset: 0; opacity: 0.6; }
  100% { stroke-dashoffset: -60; opacity: 0.9; }
}
.animate-prop-line {
  animation: prop-line 2.5s linear infinite;
}
@keyframes ambient-line {
  0%   { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -90; }
}
.animate-ambient-line {
  animation: ambient-line 8s linear infinite;
}
@keyframes igniting {
  0%   { transform: translateX(var(--tx, 0px)) scale(1); filter: brightness(1); }
  30%  { transform: translateX(var(--tx, 0px)) scale(1.12); filter: brightness(1.4); }
  100% { transform: translateX(var(--tx, 0px)) scale(1); filter: brightness(1); }
}
.animate-igniting {
  animation: igniting 1.1s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
@keyframes backdrop-breathe {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.78; }
}
.animate-backdrop-breathe {
  animation: backdrop-breathe 6s ease-in-out infinite;
}
`;
